import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { AdminApiService, AdminUser, SystemStats, GameRecord, Milestone } from '../../services/adminApiService';
import { GameHistoryViewer } from './GameHistoryViewer';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import AdminPuzzleCreator from './AdminPuzzleCreator';
import { Sandbox } from '../Sandbox';
import { copyBoard } from '../../game/logic/board';
import { checkForYugos, processYugos } from '../../game/logic/yugos';
import type { Cell } from '../../game/types';
import { formatPuzzleDisplayTitle } from '../../services/puzzleService';
import AdminTournamentManager from './AdminTournamentManager';
import './AdminDashboard.css';

// Helper function to format game timestamps in user's local timezone
// SQLite stores timestamps in UTC, so we need to parse them as UTC and convert to local
const formatGameTimestamp = (timestamp: string, format: 'date' | 'time' | 'datetime' = 'datetime'): string => {
  if (!timestamp) return '';
  
  // If timestamp doesn't have timezone info, assume it's UTC (SQLite format)
  // SQLite format: "YYYY-MM-DD HH:MM:SS"
  let date: Date;
  if (timestamp.includes('T') || timestamp.includes('Z') || timestamp.includes('+')) {
    // Already has timezone info (ISO format)
    date = new Date(timestamp);
  } else {
    // SQLite format without timezone - treat as UTC
    date = new Date(timestamp + ' UTC');
  }
  
  // Format based on requested format
  if (format === 'date') {
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } else if (format === 'time') {
    return date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } else {
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }
};

interface AdminDashboardProps {
  socket?: Socket | null;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ socket = null }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [userGames, setUserGames] = useState<GameRecord[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'info' } | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', is_admin: false, searchable: true });
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAdmin, setFilterAdmin] = useState<'all' | 'admin' | 'user'>('all');
  const [filterOnline, setFilterOnline] = useState<'all' | 'online' | 'offline'>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [minGames, setMinGames] = useState<string>('');
  const [maxGames, setMaxGames] = useState<string>('');
  const [sortBy, setSortBy] = useState<'name' | 'wins' | 'losses' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'users' | 'games' | 'analytics' | 'puzzles' | 'submissions' | 'tournaments'>('users');
  const [pendingCount, setPendingCount] = useState(0);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [reviewPuzzle, setReviewPuzzle] = useState<any | null>(null);
  const [solutionStepIndex, setSolutionStepIndex] = useState(-1);
  const [reviewBoard, setReviewBoard] = useState<(Cell | null)[][] | null>(null);

  useEffect(() => {
    // Check if admin token exists
    const token = AdminApiService.getToken();
    if (!token) {
      console.error('[AdminDashboard] No admin token found! Redirecting to login...');
      setError('Not authenticated. Please log in.');
      // Force re-render to show login
      window.location.reload();
      return;
    }
    console.log('[AdminDashboard] Admin token found, loading data...');
    loadData();
    loadPendingCount();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResult, statsResult, milestonesResult] = await Promise.all([
        AdminApiService.getAllUsers(),
        AdminApiService.getSystemStats(),
        AdminApiService.getMilestones(),
      ]);

      if (usersResult.error) {
        setError(usersResult.error);
      } else {
        setUsers(usersResult.users);
        setFilteredUsers(usersResult.users);
      }

      if (statsResult.error) {
        console.error('Failed to load stats:', statsResult.error);
      } else {
        setStats(statsResult.stats);
      }

      if (milestonesResult.error) {
        console.error('Failed to load milestones:', milestonesResult.error);
      } else {
        console.log('📊 Milestones loaded:', milestonesResult.milestones);
        const previousMilestones = milestones;
        const newMilestones = milestonesResult.milestones;
        
        // Check for newly reached milestones (not notified yet)
        if (previousMilestones.length > 0) {
          newMilestones.forEach(milestone => {
            const wasAlreadyReached = previousMilestones.some(m => m.value === milestone.value);
            if (!wasAlreadyReached && !milestone.notified) {
              // Show toast for new milestone
              setToast({
                message: `🎉 Milestone Reached: ${milestone.formatted} Games!`,
                type: 'success'
              });
              // Auto-hide toast after 5 seconds
              setTimeout(() => setToast(null), 5000);
            }
          });
        }
        
        setMilestones(newMilestones);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCount = async () => {
    const result = await AdminApiService.getPendingSubmissionCount();
    if (!result.error) setPendingCount(result.count);
  };

  const loadSubmissions = async () => {
    setLoadingSubmissions(true);
    const result = await AdminApiService.getPendingSubmissions();
    if (!result.error) setSubmissions(result.puzzles);
    setLoadingSubmissions(false);
  };

  const handleApprove = async (id: number) => {
    const result = await AdminApiService.approvePuzzle(id, reviewMessage || undefined);
    if (result.success) {
      setToast({ message: `Puzzle #${id} approved!`, type: 'success' });
      setTimeout(() => setToast(null), 4000);
      setReviewingId(null);
      setReviewMessage('');
      loadSubmissions();
      loadPendingCount();
    } else {
      setToast({ message: result.error || 'Failed to approve', type: 'info' });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleReject = async (id: number) => {
    if (!reviewMessage.trim()) {
      setToast({ message: 'Please provide a reason for rejection', type: 'info' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    const result = await AdminApiService.rejectPuzzle(id, reviewMessage);
    if (result.success) {
      setToast({ message: `Puzzle #${id} rejected`, type: 'info' });
      setTimeout(() => setToast(null), 4000);
      setReviewingId(null);
      setReviewMessage('');
      loadSubmissions();
      loadPendingCount();
    } else {
      setToast({ message: result.error || 'Failed to reject', type: 'info' });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const replayMoves = useCallback((initialBoard: (Cell | null)[][], moves: any[], upToIndex: number) => {
    const board = copyBoard(initialBoard);
    for (let i = 0; i <= upToIndex; i++) {
      const m = moves[i];
      if (!m || m.row < 0 || m.row >= 8 || m.col < 0 || m.col >= 8) continue;
      board[m.row][m.col] = { color: m.player, isYugo: false };
      const yugos = checkForYugos(board, m.row, m.col, m.player);
      const { yugoFormed } = processYugos(board, yugos, m.row, m.col);
      if (yugoFormed) {
        board[m.row][m.col] = { color: m.player, isYugo: true };
      }
    }
    return board;
  }, []);

  const openReview = useCallback((puzzle: any) => {
    setReviewPuzzle(puzzle);
    setReviewingId(puzzle.id);
    setReviewMessage('');
    setSolutionStepIndex(-1);
    setReviewBoard(puzzle.initial_board ? copyBoard(puzzle.initial_board) : null);
  }, []);

  const closeReview = useCallback(() => {
    setReviewPuzzle(null);
    setReviewingId(null);
    setReviewMessage('');
    setSolutionStepIndex(-1);
    setReviewBoard(null);
  }, []);

  const stepSolution = useCallback((direction: 'next' | 'prev' | 'reset') => {
    if (!reviewPuzzle?.initial_board || !reviewPuzzle?.solution_moves) return;
    const moves = reviewPuzzle.solution_moves;
    let newIdx = solutionStepIndex;
    if (direction === 'reset') newIdx = -1;
    else if (direction === 'next') newIdx = Math.min(solutionStepIndex + 1, moves.length - 1);
    else if (direction === 'prev') newIdx = Math.max(solutionStepIndex - 1, -1);
    setSolutionStepIndex(newIdx);
    setReviewBoard(newIdx < 0 ? copyBoard(reviewPuzzle.initial_board) : replayMoves(reviewPuzzle.initial_board, moves, newIdx));
  }, [reviewPuzzle, solutionStepIndex, replayMoves]);

  // Load submissions when tab is selected
  useEffect(() => {
    if (activeTab === 'submissions') loadSubmissions();
  }, [activeTab]);

  const handleUserClick = async (user: AdminUser) => {
    setSelectedUser(user);
    const gamesResult = await AdminApiService.getUserGames(user.id);
    if (!gamesResult.error) {
      console.log('🎮 User games loaded:', gamesResult.games);
      console.log('🎮 Games with game_id:', gamesResult.games.filter(g => g.game_id));
      setUserGames(gamesResult.games);
    }
  };

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      is_admin: user.is_admin === 1 || user.is_admin === true,
      searchable: user.searchable !== 0 && user.searchable !== false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    const updates: any = {};
    if (editForm.username !== editingUser.username) updates.username = editForm.username;
    if (editForm.email !== editingUser.email) updates.email = editForm.email;
    if (editForm.is_admin !== editingUser.is_admin) updates.is_admin = editForm.is_admin;
    const wasSearchable = editingUser.searchable !== 0 && editingUser.searchable !== false;
    if (editForm.searchable !== wasSearchable) updates.searchable = editForm.searchable;

    if (Object.keys(updates).length === 0) {
      setEditingUser(null);
      return;
    }

    const result = await AdminApiService.updateUser(editingUser.id, updates);
    if (!result.error && result.user) {
      const merged = { ...editingUser, ...result.user };
      setUsers(users.map(u => u.id === editingUser.id ? merged : u));
      if (selectedUser?.id === editingUser.id) {
        setSelectedUser(merged);
      }
      setEditingUser(null);
    } else {
      alert(result.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    const result = await AdminApiService.deleteUser(userId);
    if (result.success) {
      const updatedUsers = users.filter(u => u.id !== userId);
      setUsers(updatedUsers);
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
        setUserGames([]);
      }
    } else {
      alert(result.error || 'Failed to delete user');
    }
  };

  const handleLogout = () => {
    AdminApiService.logout();
    window.location.href = '/';
  };

  const exportToCSV = () => {
    const escapeCSV = (value: string | number | boolean | null | undefined): string => {
      const str = value == null ? '' : String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = ['ID', 'Username', 'Email', 'Registered', 'Country', 'Wins', 'Losses', 'Draws', 'Games Played', 'Win Rate', 'Admin', 'Email Verified'];
    const rows = filteredUsers.map(user => {
      const gamesPlayed = user.wins + user.losses + user.draws;
      const winRate = gamesPlayed > 0 ? ((user.wins / gamesPlayed) * 100).toFixed(1) + '%' : '0%';
      const isAdmin = user.is_admin === 1 || user.is_admin === true;
      const isVerified = user.email_verified === 1 || user.email_verified === true;
      return [
        user.id,
        user.username,
        user.email,
        user.created_at ? new Date(user.created_at.includes('T') || user.created_at.includes('Z') ? user.created_at : user.created_at + ' UTC').toISOString().split('T')[0] : '',
        user.country_name || '',
        user.wins,
        user.losses,
        user.draws,
        gamesPlayed,
        winRate,
        isAdmin ? 'Yes' : 'No',
        isVerified ? 'Yes' : 'No',
      ].map(escapeCSV).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `migoyugo-users-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Apply search, filters, and sorting
  useEffect(() => {
    let filtered = [...users];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.id.toString().includes(query)
      );
    }

    // Apply admin status filter
    if (filterAdmin !== 'all') {
      filtered = filtered.filter(user => {
        const isAdmin = user.is_admin === 1 || user.is_admin === true;
        return filterAdmin === 'admin' ? isAdmin : !isAdmin;
      });
    }

    // Apply online status filter
    if (filterOnline !== 'all') {
      filtered = filtered.filter(user => {
        const isOnline = user.is_online === 1 || user.is_online === true;
        return filterOnline === 'online' ? isOnline : !isOnline;
      });
    }

    // Apply date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(user => {
        const userDate = new Date(user.created_at);
        return userDate >= fromDate;
      });
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      // Set to end of day for inclusive filtering
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(user => {
        const userDate = new Date(user.created_at);
        return userDate <= toDate;
      });
    }

    // Apply game count filter
    if (minGames) {
      const min = parseInt(minGames);
      if (!isNaN(min)) {
        filtered = filtered.filter(user => {
          const totalGames = user.wins + user.losses + user.draws;
          return totalGames >= min;
        });
      }
    }
    if (maxGames) {
      const max = parseInt(maxGames);
      if (!isNaN(max)) {
        filtered = filtered.filter(user => {
          const totalGames = user.wins + user.losses + user.draws;
          return totalGames <= max;
        });
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.username.localeCompare(b.username);
          break;
        case 'wins':
          comparison = a.wins - b.wins;
          break;
        case 'losses':
          comparison = a.losses - b.losses;
          break;
        case 'date':
        default:
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredUsers(filtered);
  }, [users, searchQuery, filterAdmin, filterOnline, dateFrom, dateTo, minGames, maxGames, sortBy, sortOrder]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>Admin Dashboard</h1>
          <div className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              className={`admin-tab ${activeTab === 'games' ? 'active' : ''}`}
              onClick={() => setActiveTab('games')}
            >
              Game History
            </button>
            <button
              className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
            <button
              className={`admin-tab ${activeTab === 'puzzles' ? 'active' : ''}`}
              onClick={() => setActiveTab('puzzles')}
            >
              Puzzles
            </button>
            <button
              className={`admin-tab ${activeTab === 'tournaments' ? 'active' : ''}`}
              onClick={() => setActiveTab('tournaments')}
            >
              Tournaments
            </button>
            <button
              className={`admin-tab ${activeTab === 'submissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('submissions')}
              style={{ position: 'relative' }}
            >
              Submissions
              {pendingCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: -4,
                  right: -6,
                  background: '#F44336',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>{pendingCount}</span>
              )}
            </button>
          </div>
        </div>
        <button onClick={handleLogout} className="admin-logout-button">Logout</button>
      </div>

      {error && <div className="admin-error-banner">{error}</div>}

      {/* Toast Notification */}
      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="admin-toast-close">×</button>
        </div>
      )}

      {stats && activeTab !== 'puzzles' && activeTab !== 'submissions' && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalUsers}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalGames}</div>
            <div className="admin-stat-label">Total Games</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.onlineUsers}</div>
            <div className="admin-stat-label">Online Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.activeGames}</div>
            <div className="admin-stat-label">Active Games</div>
          </div>
        </div>
      )}

      {activeTab === 'games' ? (
        <GameHistoryViewer />
      ) : activeTab === 'analytics' ? (
        <div className="admin-analytics-container">
          <AnalyticsDashboard />
        </div>
      ) : activeTab === 'puzzles' ? (
        <AdminPuzzleCreator />
      ) : activeTab === 'tournaments' ? (
        <AdminTournamentManager users={users} socket={socket} />
      ) : activeTab === 'submissions' ? (
        reviewPuzzle ? (
          /* ── Full puzzle review view ── */
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: '16px', padding: '16px' }}>
            {/* Left: Sandbox with board */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Sandbox
                key={`review-${reviewPuzzle.id}-${solutionStepIndex}`}
                onStartGame={() => {}}
                onBack={() => {}}
                initialBoard={reviewBoard || undefined}
                initialStartingPlayer={reviewPuzzle.starting_player || 'white'}
              />
            </div>
            {/* Right: info + solution stepper + approve/reject */}
            <div style={{ flex: '0 0 320px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', padding: '4px' }}>
              <button
                onClick={closeReview}
                style={{ alignSelf: 'flex-start', padding: '6px 14px', backgroundColor: 'transparent', color: '#999', border: '1px solid #333', borderRadius: '4px', fontSize: '13px', cursor: 'pointer' }}
              >&larr; Back to list</button>
              <h2 style={{ margin: 0, fontSize: '18px' }}>{formatPuzzleDisplayTitle(reviewPuzzle)}</h2>
              <div style={{ fontSize: '12px', color: '#999', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <span>By <strong>{reviewPuzzle.creator_username}</strong></span>
                <span style={{ color: reviewPuzzle.difficulty === 'easy' ? '#4CAF50' : reviewPuzzle.difficulty === 'medium' ? '#FF9800' : reviewPuzzle.difficulty === 'hard' ? '#F44336' : '#9C27B0', fontWeight: 600, textTransform: 'uppercase' }}>{reviewPuzzle.difficulty}</span>
                <span>{reviewPuzzle.starting_player} to move</span>
              </div>
              {reviewPuzzle.description && <p style={{ margin: 0, fontSize: '13px', color: '#ccc' }}>{reviewPuzzle.description}</p>}

              {/* Solution stepper */}
              <div style={{ padding: '12px', backgroundColor: 'var(--card-bg, #2a2a2a)', border: '1px solid var(--border-color, #333)', borderRadius: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '10px' }}>
                  Solution ({reviewPuzzle.solution_moves ? reviewPuzzle.solution_moves.length : 0} moves)
                </div>
                {reviewPuzzle.solution_moves && reviewPuzzle.solution_moves.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                      <button onClick={() => stepSolution('reset')} disabled={solutionStepIndex < 0}
                        style={{ padding: '6px 12px', backgroundColor: 'var(--bg-color, #1a1a1a)', color: '#fff', border: '1px solid #333', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', opacity: solutionStepIndex < 0 ? 0.4 : 1 }}
                      >Reset</button>
                      <button onClick={() => stepSolution('prev')} disabled={solutionStepIndex < 0}
                        style={{ padding: '6px 12px', backgroundColor: 'var(--bg-color, #1a1a1a)', color: '#fff', border: '1px solid #333', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', opacity: solutionStepIndex < 0 ? 0.4 : 1 }}
                      >&larr; Prev</button>
                      <button onClick={() => stepSolution('next')} disabled={solutionStepIndex >= reviewPuzzle.solution_moves.length - 1}
                        style={{ padding: '6px 12px', backgroundColor: 'var(--primary-color, #4CAF50)', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600, opacity: solutionStepIndex >= reviewPuzzle.solution_moves.length - 1 ? 0.4 : 1 }}
                      >Next &rarr;</button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '8px' }}>
                      {solutionStepIndex < 0 ? 'Initial position' : `Move ${solutionStepIndex + 1} of ${reviewPuzzle.solution_moves.length}`}
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {reviewPuzzle.solution_moves.map((m: any, i: number) => (
                        <div key={i} onClick={() => { setSolutionStepIndex(i); setReviewBoard(replayMoves(reviewPuzzle.initial_board, reviewPuzzle.solution_moves, i)); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
                            backgroundColor: i === solutionStepIndex ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
                            border: i === solutionStepIndex ? '1px solid #4CAF50' : '1px solid transparent',
                          }}>
                          <span style={{ color: '#999', fontWeight: 600, minWidth: '24px' }}>{i + 1}.</span>
                          <span style={{ fontWeight: 500 }}>{m.player === 'white' ? 'White' : 'Black'}</span>
                          <span style={{ color: '#4CAF50', fontWeight: 600, fontFamily: 'monospace' }}>{String.fromCharCode(97 + m.col)}{8 - m.row}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>No solution moves provided.</div>
                )}
              </div>

              {/* Approve / Reject */}
              <div style={{ padding: '12px', backgroundColor: 'var(--card-bg, #2a2a2a)', border: '1px solid var(--border-color, #333)', borderRadius: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Decision</div>
                <textarea
                  value={reviewMessage}
                  onChange={(e) => setReviewMessage(e.target.value)}
                  placeholder="Message to creator (required for rejection)"
                  rows={3}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '8px',
                    backgroundColor: 'var(--bg-color, #1a1a1a)', border: '1px solid var(--border-color, #333)',
                    borderRadius: '4px', color: 'var(--text-color, #fff)', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '8px',
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={async () => { await handleApprove(reviewPuzzle.id); closeReview(); }}
                    style={{ flex: 1, padding: '10px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >Approve</button>
                  <button onClick={async () => { await handleReject(reviewPuzzle.id); closeReview(); }}
                    style={{ flex: 1, padding: '10px', backgroundColor: 'rgba(244,67,54,0.2)', color: '#F44336', border: '1px solid #F44336', borderRadius: '4px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >Reject</button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── Submissions list ── */
          <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Puzzle Submissions ({submissions.length} pending)</h2>
            {loadingSubmissions ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Loading submissions...</div>
            ) : submissions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No pending submissions.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {submissions.map((puzzle: any) => (
                  <div key={puzzle.id} onClick={() => openReview(puzzle)} style={{
                    padding: '16px',
                    backgroundColor: 'var(--card-bg, #2a2a2a)',
                    border: '1px solid var(--border-color, #333)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#4CAF50')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border-color, #333)')}
                  >
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '16px' }}>{formatPuzzleDisplayTitle(puzzle)}</h3>
                    <div style={{ fontSize: '12px', color: '#999', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span>By <strong>{puzzle.creator_username}</strong></span>
                      <span style={{ color: puzzle.difficulty === 'easy' ? '#4CAF50' : puzzle.difficulty === 'medium' ? '#FF9800' : puzzle.difficulty === 'hard' ? '#F44336' : '#9C27B0', fontWeight: 600, textTransform: 'uppercase' }}>{puzzle.difficulty}</span>
                      <span>{puzzle.solution_moves ? `${puzzle.solution_moves.length} moves` : 'No solution'}</span>
                      <span>{puzzle.starting_player} to move</span>
                      {puzzle.submitted_at && <span>Submitted {new Date(puzzle.submitted_at).toLocaleDateString()}</span>}
                    </div>
                    {puzzle.description && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#ccc' }}>{puzzle.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      ) : (
        <>
          {/* Search and Filter Controls - Full Width Container */}
          <div className="admin-search-filters-container">
            <div className="admin-search-filters-row">
              <div className="admin-search-box">
                <input
                  type="text"
                  placeholder="Search by username, email, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="admin-search-input"
                />
              </div>
              
              <div className="admin-filter-group">
                <label>Admin Status:</label>
                <select
                  value={filterAdmin}
                  onChange={(e) => setFilterAdmin(e.target.value as 'all' | 'admin' | 'user')}
                  className="admin-filter-select"
                >
                  <option value="all">All Users</option>
                  <option value="admin">Admins Only</option>
                  <option value="user">Regular Users</option>
                </select>
              </div>
              
              <div className="admin-filter-group">
                <label>Online Status:</label>
                <select
                  value={filterOnline}
                  onChange={(e) => setFilterOnline(e.target.value as 'all' | 'online' | 'offline')}
                  className="admin-filter-select"
                >
                  <option value="all">All</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>
              
              <div className="admin-filter-group">
                <label>Sort By:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'wins' | 'losses' | 'date')}
                  className="admin-filter-select"
                >
                  <option value="date">Registration Date</option>
                  <option value="name">Username</option>
                  <option value="wins">Wins</option>
                  <option value="losses">Losses</option>
                </select>
              </div>
              
              <div className="admin-filter-group">
                <label>Order:</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="admin-filter-select"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>

              <div className="admin-filter-group">
                <label>Registration From:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="admin-filter-input"
                />
              </div>
              
              <div className="admin-filter-group">
                <label>Registration To:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="admin-filter-input"
                />
              </div>
              
              <div className="admin-filter-group">
                <label>Min Games:</label>
                <input
                  type="number"
                  min="0"
                  value={minGames}
                  onChange={(e) => setMinGames(e.target.value)}
                  placeholder="0"
                  className="admin-filter-input"
                />
              </div>
              
              <div className="admin-filter-group">
                <label>Max Games:</label>
                <input
                  type="number"
                  min="0"
                  value={maxGames}
                  onChange={(e) => setMaxGames(e.target.value)}
                  placeholder="∞"
                  className="admin-filter-input"
                />
              </div>

              <div className="admin-filter-group">
                <label>&nbsp;</label>
                <button onClick={exportToCSV} className="admin-export-button">
                  Export CSV ({filteredUsers.length})
                </button>
              </div>
            </div>
          </div>

          {/* Users List and Details - Two Column Grid */}
          <div className="admin-content-grid">
            <div className="admin-users-panel">
              <div className="admin-users-list">
            {filteredUsers.length === 0 ? (
              <div className="admin-no-users">No users found matching your criteria</div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`admin-user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                  onClick={() => handleUserClick(user)}
                >
                  <div className="admin-user-info">
                    <div className="admin-user-name-row">
                      <div className="admin-user-name">
                        <span className={`admin-status-dot ${(user.is_online === 1 || user.is_online === true) ? 'online' : 'offline'}`}></span>
                        {user.username}
                        {(user.is_admin === 1 || user.is_admin === true) && <span className="admin-badge">Admin</span>}
                        {(user.searchable === 0 || user.searchable === false) && <span className="admin-badge hidden">Hidden</span>}
                        <span className="admin-user-stats-inline">{user.wins}W / {user.losses}L / {user.draws}D</span>
                        {user.country_name && <span style={{ marginLeft: '10px', fontSize: '0.85em', color: '#666' }}>📍 {user.country_name}</span>}
                      </div>
                    </div>
                    <div className="admin-user-email">{user.email}</div>
                  </div>
                  <div className="admin-user-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditUser(user);
                      }}
                      className="admin-action-button edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user.id);
                      }}
                      className="admin-action-button delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-details-panel">
          {selectedUser ? (
            <>
              <div className="admin-user-details">
                <div className="admin-detail-item">
                  <strong>Status:</strong> 
                  <span className={`admin-status-indicator ${(selectedUser.is_online === 1 || selectedUser.is_online === true) ? 'online' : 'offline'}`}></span>
                  {(selectedUser.is_online === 1 || selectedUser.is_online === true) ? 'Online' : 'Offline'} {(selectedUser.in_game === 1 || selectedUser.in_game === true) ? '(In Game)' : ''}
                </div>
                <div className="admin-detail-item">
                  <strong>Last Online:</strong> {(selectedUser.is_online === 1 || selectedUser.is_online === true) ? 'Currently Online' : (selectedUser.last_seen ? formatGameTimestamp(selectedUser.last_seen) : 'Never')}
                </div>
                <div className="admin-detail-item">
                  <strong>ID:</strong> {selectedUser.id}
                </div>
                <div className="admin-detail-item">
                  <strong>Username:</strong> {selectedUser.username}
                </div>
                <div className="admin-detail-item">
                  <strong>Email:</strong> {selectedUser.email}
                </div>
                <div className="admin-detail-item">
                  <strong>Admin:</strong> {(selectedUser.is_admin === 1 || selectedUser.is_admin === true) ? 'Yes' : 'No'}
                </div>
                <div className="admin-detail-item">
                  <strong>Visible to members:</strong> {(selectedUser.searchable === 0 || selectedUser.searchable === false) ? 'No (hidden)' : 'Yes'}
                </div>
                <div className="admin-detail-item">
                  <strong>Wins:</strong> {selectedUser.wins}
                </div>
                <div className="admin-detail-item">
                  <strong>Losses:</strong> {selectedUser.losses}
                </div>
                <div className="admin-detail-item">
                  <strong>Draws:</strong> {selectedUser.draws}
                </div>
                <div className="admin-detail-item">
                  <strong>Current Streak:</strong> {selectedUser.current_streak} ({selectedUser.streak_type || 'none'})
                </div>
                <div className="admin-detail-item">
                  <strong>Created:</strong> {formatGameTimestamp(selectedUser.created_at)}
                </div>
                {selectedUser.country_name && (
                  <div className="admin-detail-item">
                    <strong>Country:</strong> {selectedUser.country_name} {selectedUser.country_code && `(${selectedUser.country_code})`}
                  </div>
                )}
              </div>

              <div className="admin-games-section">
                <h3>Game History ({userGames.length})</h3>
                <div className="admin-games-list">
                  {userGames.length === 0 ? (
                    <div className="admin-no-games">No games found</div>
                  ) : (
                    userGames.map((game) => (
                      <div key={game.id} className="admin-game-item">
                        <div className="admin-game-result">
                          <span className={`admin-result-badge ${game.result}`}>
                            {game.result.toUpperCase()}
                          </span>
                          <span className="admin-game-opponent">vs {game.opponent}</span>
                        </div>
                        <div className="admin-game-info">
                          {game.player_color} • Match: {game.game_mode} • {formatGameTimestamp(game.timestamp)}
                        </div>
                        {game.game_id && (
                          <div className="admin-game-id">
                            Game ID: <code>{game.game_id}</code>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="admin-no-selection">Select a user to view details</div>
          )}
        </div>
      </div>
      </>
      )}

      {editingUser && (
        <div className="admin-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit User</h2>
            <div className="admin-form-group">
              <label>Username</label>
              <input
                type="text"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              />
            </div>
            <div className="admin-form-group">
              <label>Email</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div className="admin-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_admin}
                  onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                />
                Admin Access
              </label>
            </div>
            <div className="admin-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={!editForm.searchable}
                  onChange={(e) => setEditForm({ ...editForm, searchable: !e.target.checked })}
                />
                Hidden from members (not in Players, not challengeable)
              </label>
            </div>
            <div className="admin-modal-actions">
              <button onClick={handleSaveEdit} className="admin-save-button">Save</button>
              <button onClick={() => setEditingUser(null)} className="admin-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

